import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sparkles, RefreshCw, Loader2, Mail, Radio, MessageSquareQuote, Clock, ShieldAlert, Ban } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

function Section({ icon: Icon, title, items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={`whats-working-${title.toLowerCase().replace(/[\s/]+/g, '-')}`}>
      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1.5">
        <Icon className={`w-3.5 h-3.5 ${color}`} /> {title}
      </p>
      <ul className="space-y-1">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-foreground/90 leading-snug flex gap-2">
            <span className={`mt-1.5 h-1 w-1 rounded-full shrink-0 ${color.replace('text-', 'bg-')}`} />
            <span>{typeof it === 'string' ? it : `${it.objection} → ${it.counter}`}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function WhatsWorkingPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/brain/messaging-playbook');
      setData(r.data);
    } catch { /* non-critical */ }
    finally { setLoading(false); }
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/api/brain/messaging-playbook/refresh');
      const st = r.data?.status;
      if (st === 'learned') toast.success('Messaging playbook updated from your won/lost deals');
      else if (st === 'insufficient_data') toast.info(r.data?.message || 'Need more closed deals to learn');
      else toast.message(r.data?.message || 'Analysis complete');
      await load();
    } catch (e) {
      if (e?.response?.status === 403) toast.error('Upgrade to Growth to refresh the playbook');
      else toast.error('Failed to analyze messaging');
    } finally { setRefreshing(false); }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;

  const pb = data?.playbook;
  const stats = data?.stats || {};

  return (
    <Card className="border-0 shadow-sm ring-1 ring-violet-200/50" data-testid="whats-working-panel">
      <div className="h-0.5 bg-gradient-to-r from-violet-500 to-fuchsia-500" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Sparkles className="w-4.5 h-4.5 text-violet-500" />
              What's Working — Messaging Playbook
              {pb?.version > 0 && (
                <Badge variant="secondary" className="bg-violet-500/10 text-violet-600 border-0 text-xs">v{pb.version}</Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              The Brain learns what closes deals from your wins &amp; losses and applies it to every cadence + email.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="whats-working-refresh-btn">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-1.5 hidden sm:inline">Re-learn</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {!pb ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="whats-working-empty">
            <Sparkles className="w-7 h-7 mx-auto mb-2 opacity-40" />
            <p className="text-sm">
              {stats.enough_data
                ? 'Click "Re-learn" to build your messaging playbook from won/lost deals.'
                : `Close a few deals (${stats.won || 0} won, ${stats.lost || 0} lost so far) — the Brain auto-builds your playbook as wins & losses come in.`}
            </p>
          </div>
        ) : (
          <>
            {pb.summary && (
              <div className="p-3 rounded-xl bg-violet-50 dark:bg-violet-950/20 border border-violet-200/60 dark:border-violet-500/20" data-testid="whats-working-summary">
                <p className="text-sm text-foreground/90">{pb.summary}</p>
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  Learned from {pb.won_count} won · {pb.lost_count} lost · {pb.win_rate}% win rate
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              <Section icon={Mail} title="Winning Subject Lines" items={pb.winning_subject_patterns} color="text-emerald-500" />
              <Section icon={Radio} title="Channels That Convert" items={pb.winning_channels} color="text-sky-500" />
              <Section icon={MessageSquareQuote} title="Themes That Win" items={pb.winning_themes} color="text-violet-500" />
              <Section icon={Clock} title="Timing That Works" items={pb.best_timing ? [pb.best_timing] : []} color="text-amber-500" />
              <Section icon={ShieldAlert} title="Objections & Counters" items={pb.common_objections} color="text-orange-500" />
              <Section icon={Ban} title="Avoid (Lost-deal Patterns)" items={pb.losing_patterns} color="text-rose-500" />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
